import { getStartEpoch } from '@thoughtspot/custom-calendar-service';
import { convertToMilliSeconds, convertToSeconds, } from '@thoughtspot/date-format-util';
import * as dateUtil from '@thoughtspot/date-format-util';
import { isKPIAnomaliesEnabled, isSpotIQV2Enabled, } from '@thoughtspot/session-service';
import { getTimeBucket, hasCustomCalendar, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getChartSpecificCustomProps, getCustomColorSelectorArray, getXAxisColumns, isXAxisOrdinalBased, } from '../../core/chart-model-utils';
import { chartSpecificConstants } from '../../core/chart-type-specification-service';
import { getSeries, removeIncompleteDataIfPresent, removeLastIfNullAndEqualLength, } from '../../data/data-util';
import { getEnvFlags } from '../../env';
import { getEffectiveRefTuple, getIncompleteThreshold, isInCompleteKPI, isSparklineEnabled, } from './kpi-utils';
export const anomalyColorIndex = 2;
export const boundColorIndex = 3;
export const ABOVE_EXPECTED_COLOR_INDEX = 2;
export const BELOW_EXPECTED_COLOR_INDEX = 3;
export const ANOMALY_GRAY_COLOR = '#777E8B';
export const ANOMALY_BLACK_LIGHT_COLOR = '#1D232F';
export const ANOMALY_GRAY_TINT4_COLOR = '#EAEDF2';
export const ANOMALY_BAR_HEIGHT = 22;
export const EXPECTED_ANOMALY_CONSTANTS = {
    ABOVE: 'ABOVE_EXPECTED_ANOMALY',
    BELOW: 'BELOW_EXPECTED_ANOMALY',
    AS: 'AS_EXPECTED_ANOMALY',
    CAN_NOT_DETECT: 'CANNOT_DETECT_ANOMALY',
    DETECTING_ANOMALIES: 'DETECTING_ANOMALIES_V2',
};
export const getCortexData = (chartModel) => {
    return _.get(chartModel, 'data.0.cortexData');
};
export const hasCortexData = (chartModel) => {
    const cortexData = getCortexData(chartModel);
    return cortexData !== undefined && cortexData !== null;
};
export const hasCacheTokenData = (cortexData) => {
    return _.has(cortexData, 'cacheToken') && !_.isEmpty(cortexData.cacheToken);
};
export const hasInferenceData = (cortexData) => {
    return (_.has(cortexData, 'inference') &&
        _.isArray(cortexData.inference) &&
        !_.isEmpty(cortexData.inference));
};
export const hasInferenceProperty = (cortexData) => {
    return _.has(cortexData, 'inference');
};
export const hasEnoughDataForCortex = (chartModel) => Number(_.get(chartModel, 'data.0.totalRowCount')) > 5;
export const isShowAnomaliesEnabled = (chartModel) => {
    var _a, _b;
    return isKPIAnomaliesEnabled() &&
        isSparklineEnabled(chartModel) &&
        ((_b = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.showAnomalies) !== null && _b !== void 0 ? _b : true);
};
export const isShowAnomaliesSupported = (chartModel) => hasEnoughDataForCortex(chartModel) &&
    (!isShowAnomaliesEnabled(chartModel) ||
        !_.isEmpty(getCortexData(chartModel)));
export const isShowBoundsEnabled = (chartModel) => {
    var _a, _b;
    return isShowAnomaliesEnabled(chartModel) &&
        ((_b = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.showBounds) !== null && _b !== void 0 ? _b : true) &&
        !getEnvFlags().isKPIWithAnomalyV2Enabled;
};
export const hasAnomalyBounds = (chartModel) => {
    var _a, _b, _c, _d;
    return isKPIAnomaliesEnabled() &&
        isSparklineEnabled(chartModel) &&
        ((_b = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.showAnomalies) !== null && _b !== void 0 ? _b : true) &&
        ((_d = (_c = getChartSpecificCustomProps(chartModel)) === null || _c === void 0 ? void 0 : _c.showBounds) !== null && _d !== void 0 ? _d : true);
};
export const getAnomalyColor = (chartModel) => _.get(getCustomColorSelectorArray(chartModel), anomalyColorIndex, chartSpecificConstants.DEFAULT_KPI_COLORS[anomalyColorIndex]);
export const getBoundColor = (chartModel) => _.get(getCustomColorSelectorArray(chartModel), boundColorIndex, chartSpecificConstants.DEFAULT_KPI_COLORS[boundColorIndex]);
export const getAboveExpectedColor = (chartModel) => _.get(getCustomColorSelectorArray(chartModel), ABOVE_EXPECTED_COLOR_INDEX, chartSpecificConstants.DEFAULT_NEW_KPI_COLORS[ABOVE_EXPECTED_COLOR_INDEX]);
export const getBelowExpectedColor = (chartModel) => _.get(getCustomColorSelectorArray(chartModel), BELOW_EXPECTED_COLOR_INDEX, chartSpecificConstants.DEFAULT_NEW_KPI_COLORS[BELOW_EXPECTED_COLOR_INDEX]);
export const getAnomalyExpectedColor = (chartModel, lastAnomalyPoint) => {
    var _a;
    const effectiveRefTuple = getEffectiveRefTuple(chartModel);
    const diffValue = parseFloat((_a = parseFloat(_.get(effectiveRefTuple, 'relativePercentChange'))) === null || _a === void 0 ? void 0 : _a.toFixed(2));
    const textForLabel = lastAnomalyPoint
        ? `${diffValue > 0 ? 'ABOVE' : 'BELOW'}_EXPECTED_ANOMALY`
        : 'AS_EXPECTED_ANOMALY';
    let color = getAnomalyColor(chartModel);
    if (textForLabel === EXPECTED_ANOMALY_CONSTANTS.ABOVE) {
        color = getAboveExpectedColor(chartModel);
    }
    else if (textForLabel === EXPECTED_ANOMALY_CONSTANTS.BELOW) {
        color = getBelowExpectedColor(chartModel);
    }
    return color;
};
export const getSeriesData = (dataModel) => _.get(dataModel, 'series', []);
const extractSeriesData = (dataModel) => {
    var _a;
    const series = getSeriesData(dataModel);
    return Array.isArray(series) ? (_a = series[0]) === null || _a === void 0 ? void 0 : _a.data : null;
};
const getLastDataPoint = (data) => {
    return Array.isArray(data) && data.length > 0
        ? data[data.length - 1]
        : null;
};
const determineLoadingState = (chartModel, forecastPoints) => {
    if (!forecastPoints && hasCacheTokenData(getCortexData(chartModel))) {
        return true;
    }
    return false;
};
const adjustLastDataPoint = (lastDataPoint, isCustomCalendar, data) => {
    if (!lastDataPoint)
        return null;
    if (isCustomCalendar) {
        lastDataPoint.xVal = lastDataPoint.xRawValues[0];
        lastDataPoint.yVal = lastDataPoint.yRawValues[0];
        return lastDataPoint;
    }
    if (!_.isEqual(lastDataPoint.x, lastDataPoint.xRawValues[0])) {
        const sortedData = _.isObject(data === null || data === void 0 ? void 0 : data[0]) ? data : _.sortBy(data);
        const lastValue = sortedData[sortedData.length - 1];
        lastDataPoint.xVal = lastValue.xRawValues[0];
        lastDataPoint.yVal = lastValue.yRawValues[0];
        return lastDataPoint;
    }
    lastDataPoint.xVal = lastDataPoint.x;
    lastDataPoint.yVal = lastDataPoint.y;
    return lastDataPoint;
};
export const aboveExpected = (chartModel) => ({
    lineColor: getAboveExpectedColor(chartModel),
    labelColor: getAboveExpectedColor(chartModel),
    labelText: EXPECTED_ANOMALY_CONSTANTS.ABOVE,
});
export const asExpected = (defaultColor) => ({
    lineColor: defaultColor,
    labelColor: ANOMALY_BLACK_LIGHT_COLOR,
    labelText: EXPECTED_ANOMALY_CONSTANTS.AS,
});
export const belowExpected = (chartModel) => ({
    lineColor: getBelowExpectedColor(chartModel),
    labelColor: getBelowExpectedColor(chartModel),
    labelText: EXPECTED_ANOMALY_CONSTANTS.BELOW,
});
export const cannotDetect = (defaultColor, isLoading) => ({
    lineColor: defaultColor,
    labelColor: ANOMALY_GRAY_COLOR,
    labelText: isLoading
        ? EXPECTED_ANOMALY_CONSTANTS.DETECTING_ANOMALIES
        : EXPECTED_ANOMALY_CONSTANTS.CAN_NOT_DETECT,
});
export const determineAnomalyDetails = (lastPoint, forecastPoints, chartModel, defaultColor, isLoading) => {
    if (!lastPoint || !forecastPoints) {
        return cannotDetect(defaultColor, isLoading);
    }
    const lastForecast = forecastPoints[forecastPoints.length - 1];
    const lastForecastTimeMs = Number(lastForecast.time) * 1000;
    if (lastPoint.yVal > lastForecast.high) {
        return aboveExpected(chartModel);
    }
    if (lastPoint.yVal <= lastForecast.high &&
        lastPoint.yVal >= lastForecast.low) {
        return asExpected(defaultColor);
    }
    if (lastPoint.yVal < lastForecast.low) {
        return belowExpected(chartModel);
    }
    if (lastPoint.xVal !== lastForecastTimeMs) {
        return cannotDetect(defaultColor, isLoading);
    }
    return cannotDetect(defaultColor, isLoading);
};
export const getForecastDetailsWithExpectedColor = (chartModel, seriesColor, dataModel, isCustomCalendarTimeSeriesChart) => {
    const forecastPoints = getForecastPoints(chartModel);
    const seriesData = extractSeriesData(dataModel);
    const lastDataPoint = getLastDataPoint(seriesData);
    const isLoading = determineLoadingState(chartModel, forecastPoints);
    const hasIncompleteData = isInCompleteKPI(chartModel) || !isShowAnomaliesEnabled(chartModel);
    const adjustedLastPoint = adjustLastDataPoint(lastDataPoint, isCustomCalendarTimeSeriesChart, seriesData);
    const anomalyDetails = determineAnomalyDetails(adjustedLastPoint, forecastPoints, chartModel, seriesColor, isLoading);
    return {
        color: hasIncompleteData ? seriesColor : anomalyDetails.lineColor,
        labelColor: anomalyDetails.labelColor,
        label: anomalyDetails.labelText,
        lastPoint: adjustedLastPoint,
        data: seriesData,
        size: (seriesData === null || seriesData === void 0 ? void 0 : seriesData.length) || 0,
        isLoading,
    };
};
export const shouldCortexLoadAsync = (chartModel) => {
    var _a;
    return isShowAnomaliesEnabled(chartModel) &&
        !_.isEmpty((_a = getCortexData(chartModel)) === null || _a === void 0 ? void 0 : _a.cacheToken);
};
export const getCortexCacheRequest = (chartModel) => {
    var _a;
    return {
        cacheToken: (_a = getCortexData(chartModel)) === null || _a === void 0 ? void 0 : _a.cacheToken,
    };
};
const boundSeriesTemplate = {
    name: 'Anomaly bounds',
    type: 'arearange',
    visible: true,
    fillOpacity: 0.3,
    lineWidth: 0,
    zIndex: -1,
    enableMouseTracking: false,
};
export const getForecastPoints = (chartModel) => {
    const cortexData = getCortexData(chartModel);
    if (!cortexData ||
        _.isEmpty(cortexData) ||
        _.isEmpty(cortexData.inference)) {
        return undefined;
    }
    return cortexData.inference;
};
export const getCortexPoints = (chartModel) => {
    const cortexData = _.get(chartModel, 'cortexData.forecasts');
    if (!cortexData || _.isEmpty(cortexData) || _.isEmpty(cortexData.matrix)) {
        return undefined;
    }
    return cortexData;
};
export const getCortexAnomalyList = (chartModel, dataModel) => {
    var _a;
    const inferences = getForecastPoints(chartModel);
    if (!inferences) {
        return undefined;
    }
    const data = (_a = getSeries(dataModel)[0]) === null || _a === void 0 ? void 0 : _a.data;
    const times = _(data)
        .mapValues((value => { var _a; return (_a = value === null || value === void 0 ? void 0 : value.xRawValues) === null || _a === void 0 ? void 0 : _a[0]; }))
        .values()
        .value();
    let i = 0;
    const anomalyList = _(times)
        .map(time => {
        if ((i === 0 &&
            convertToMilliSeconds(inferences[0].time) !== time) ||
            i >= _.size(inferences)) {
            return false;
        }
        return inferences[i++].anomaly;
    })
        .value();
    return anomalyList;
};
export const getIncompleteThresholdValue = (chartModel, blinkHighchartConfig) => {
    const incompleteZone = blinkHighchartConfig._configOptions.incompleteZone;
    if (incompleteZone.disabled) {
        return undefined;
    }
    const xAxisColumns = getXAxisColumns(chartModel);
    const getDefaultIncompleteThreshold = dateUtil.getTimeBucketStart.bind(this, Date.now());
    const timeBucket = getTimeBucket(xAxisColumns[0]);
    return incompleteZone.getIncompleteThreshold
        ? incompleteZone.getIncompleteThreshold(timeBucket)
        : getDefaultIncompleteThreshold(timeBucket);
};
const getXValueFromOriginalSeriesData = (originalSeriesData, xValue) => {
    var _a;
    return (_a = originalSeriesData.find(seriesData => (seriesData === null || seriesData === void 0 ? void 0 : seriesData.xRawValues[0]) === xValue)) === null || _a === void 0 ? void 0 : _a.x;
};
export const isPointAnomaly = (chartModel, time) => {
    const inferences = getForecastPoints(chartModel);
    if (!inferences || !time) {
        return undefined;
    }
    const timeSecs = convertToSeconds(time);
    const inference = _(inferences).find(infer => Number(infer.time) === timeSecs);
    const incompleteThreshold = getIncompleteThreshold(chartModel);
    return !!(inference === null || inference === void 0 ? void 0 : inference.anomaly) && time <= incompleteThreshold;
};
export const addCortexBoundSeries = (chartModel, blinkHighchartConfig) => {
    var _a, _b, _c, _d, _e;
    const inferences = getForecastPoints(chartModel);
    const forcastPoints = getCortexPoints(chartModel);
    const originalSeriesData = (_a = blinkHighchartConfig._highchartConfig.series[0]) === null || _a === void 0 ? void 0 : _a.data;
    if (isShowBoundsEnabled(chartModel) && inferences && originalSeriesData) {
        const incompleteThreshold = (_b = getIncompleteThresholdValue(chartModel, blinkHighchartConfig)) !== null && _b !== void 0 ? _b : Number.MAX_SAFE_INTEGER;
        const rangeValues = _(inferences)
            .filter(inference => convertToMilliSeconds(inference.time) <=
            incompleteThreshold)
            .mapValues((inference => {
            return [
                getXValueFromOriginalSeriesData(originalSeriesData, convertToMilliSeconds(inference.time)),
                inference.low,
                inference.high,
            ];
        }))
            .values()
            .value();
        blinkHighchartConfig._highchartConfig.series.push({
            ...boundSeriesTemplate,
            color: getBoundColor(chartModel),
            data: rangeValues,
        });
    }
    if (forcastPoints && isSpotIQV2Enabled()) {
        const lastChartPoint = removeLastIfNullAndEqualLength(chartModel);
        const xAxisColumn = _.first(getXAxisColumns(chartModel));
        const incompleteThreshold = getIncompleteThreshold(chartModel);
        const incompleteThresholdInSeconds = convertToSeconds(incompleteThreshold);
        const sortInfo = ((_c = chartModel === null || chartModel === void 0 ? void 0 : chartModel.sortOrder) === null || _c === void 0 ? void 0 : _c.length)
            ? chartModel === null || chartModel === void 0 ? void 0 : chartModel.sortOrder
            : chartModel === null || chartModel === void 0 ? void 0 : chartModel.sortInfo;
        const isXAxisSortAsc = (_e = (_d = sortInfo === null || sortInfo === void 0 ? void 0 : sortInfo.find(_ => _.columnId === xAxisColumn.id)) === null || _d === void 0 ? void 0 : _d.sortAscending) !== null && _e !== void 0 ? _e : true;
        let lastX = isXAxisSortAsc
            ? lastChartPoint[0].dataValue[lastChartPoint[0].dataValue.length - 1]
            : lastChartPoint[0].dataValue[0];
        let lastY = isXAxisSortAsc
            ? lastChartPoint[1].dataValue[lastChartPoint[1].dataValue.length - 1]
            : lastChartPoint[1].dataValue[0];
        const upperIndex = forcastPoints.matrix.columnInfos.findIndex(_column => _column.columnType === 'TARGET_UPPER');
        const lowerIndex = forcastPoints.matrix.columnInfos.findIndex(_column => _column.columnType === 'TARGET_LOWER');
        const timeAxis = forcastPoints.matrix.columnInfos.findIndex(_column => _column.columnType === 'TIME');
        const isOrdinalXAxis = isXAxisOrdinalBased(chartModel);
        if (isOrdinalXAxis) {
            lastX = lastChartPoint[0].dataValue.length - 1;
        }
        else if (hasCustomCalendar(xAxisColumn)) {
            lastX = getStartEpoch(lastX);
        }
        if (getEnvFlags().isForecastIncompletePointsHidden &&
            lastX > incompleteThresholdInSeconds) {
            const filteredPoints = lastChartPoint[0].dataValue.filter(point => {
                const x = hasCustomCalendar(xAxisColumn)
                    ? getStartEpoch(point)
                    : point;
                return x <= incompleteThresholdInSeconds;
            });
            lastX = isXAxisSortAsc
                ? filteredPoints[filteredPoints.length - 1]
                : filteredPoints[0];
            if (hasCustomCalendar(xAxisColumn)) {
                lastX = getStartEpoch(lastX);
            }
            lastY = isXAxisSortAsc
                ? lastChartPoint[1].dataValue[filteredPoints.length - 1]
                : lastChartPoint[1].dataValue[0];
            removeIncompleteDataIfPresent(chartModel, isXAxisSortAsc, hasCustomCalendar(xAxisColumn), incompleteThresholdInSeconds);
        }
        const rangeValues = _(forcastPoints.matrix.rows.slice(0))
            .mapValues(((_point, index) => {
            return [
                isOrdinalXAxis
                    ? lastX + parseInt(index, 10) + 1
                    : convertToMilliSeconds(_point.columns[timeAxis].intVal),
                _point.columns[upperIndex].doubleVal,
                _point.columns[lowerIndex].doubleVal,
            ];
        }))
            .values()
            .value();
        rangeValues.unshift([
            isOrdinalXAxis ? lastX : convertToMilliSeconds(lastX),
            lastY,
            lastY,
        ]);
        blinkHighchartConfig._highchartConfig.series.push({
            ...boundSeriesTemplate,
            name: 'Forecast bound',
            color: getBoundColor(chartModel),
            data: rangeValues,
        });
    }
};
export const getCortexPoint = (chartModel, chartPoint) => {
    const inferences = getForecastPoints(chartModel);
    if (!inferences || _.isEmpty(chartPoint === null || chartPoint === void 0 ? void 0 : chartPoint.selectedAttributes)) {
        return undefined;
    }
    const selectedTime = hasCustomCalendar(chartPoint.selectedAttributes[0].column)
        ? getStartEpoch(chartPoint.selectedAttributes[0].value)
        : chartPoint.selectedAttributes[0].value;
    const inference = _(inferences).find(infer => Number(infer.time) === selectedTime);
    const incompleteThreshold = getIncompleteThreshold(chartModel);
    return {
        lowerBound: inference === null || inference === void 0 ? void 0 : inference.low,
        expected: inference === null || inference === void 0 ? void 0 : inference.pred,
        upperBound: inference === null || inference === void 0 ? void 0 : inference.high,
        anomaly: (inference === null || inference === void 0 ? void 0 : inference.anomaly) &&
            convertToMilliSeconds(selectedTime) <= incompleteThreshold,
    };
};
//# sourceMappingURL=cortex-utils.js.map